import { useState } from "react";
import { useApp, useUiDispatch } from "@/app/AppContext";
import type { TranslationKey } from "@/config/translationKeys";
import { useI18n } from "@/i18n/useI18n";

const COLLAPSED_STORAGE_KEY = "tiltak_respondent_intro_collapsed";

const STEPS = [
  {
    status: "draft",
    badge: "statusDraft",
    title: "respondentStepRegisterTitle",
    text: "respondentStepRegisterText",
  },
  {
    status: "submitted",
    badge: "statusSubmitted",
    title: "respondentStepReviewTitle",
    text: "respondentStepReviewText",
  },
  {
    status: "approved",
    badge: "statusApproved",
    title: "respondentStepPublishedTitle",
    text: "respondentStepPublishedText",
  },
] as const satisfies readonly {
  status: string;
  badge: TranslationKey;
  title: TranslationKey;
  text: TranslationKey;
}[];

function readStoredCollapsed(): boolean | null {
  try {
    const value = window.localStorage.getItem(COLLAPSED_STORAGE_KEY);
    return value === null ? null : value === "1";
  } catch {
    return null;
  }
}

function storeCollapsed(collapsed: boolean): void {
  try {
    window.localStorage.setItem(COLLAPSED_STORAGE_KEY, collapsed ? "1" : "0");
  } catch {
    // Storage may be unavailable (private mode); the guide then opens by default.
  }
}

function WarningIcon({ size }: { size: number }) {
  return (
    <svg
      className="respondent-intro-warning-icon"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 3l9.5 17h-19z" />
      <path d="M12 10v4" />
      <path d="M12 17.5v.01" />
    </svg>
  );
}

function Chevron({ up }: { up: boolean }) {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      aria-hidden="true"
    >
      <path d={up ? "M6 15l6-6 6 6" : "M6 9l6 6 6-6"} />
    </svg>
  );
}

/**
 * Guide shown above the list for respondents: welcome text, the
 * draft → submitted → approved workflow, the sensitive-data notice and the
 * "New intervention" button. Returning users can collapse it to one row.
 */
export function RespondentIntro() {
  const { t } = useI18n();
  const { user, data } = useApp();
  const dispatch = useUiDispatch();
  // Users who have already registered something start with the guide collapsed.
  const [collapsed, setCollapsed] = useState(
    () =>
      readStoredCollapsed() ??
      data.dataset.items.some((item) => !!user && item.submittedBy === user.id),
  );

  const toggle = () => {
    setCollapsed(!collapsed);
    storeCollapsed(!collapsed);
  };

  const newButton = (
    <button
      type="button"
      className="new-intervention-btn"
      onClick={() => dispatch({ type: "createIntervention" })}
    >
      {t("newInterventionButton")}
    </button>
  );

  if (collapsed) {
    return (
      <section className="respondent-intro collapsed" aria-labelledby="respondent-list-title">
        {/* Grid areas keep the title on the same baseline as the toggle. */}
        <h2 id="respondent-list-title" className="respondent-intro-list-title">
          {t("respondentListTitle")}
        </h2>
        <span className="respondent-intro-warning-inline">
          <WarningIcon size={16} />
          {t("respondentSensitiveTitle")}
        </span>
        <button
          type="button"
          className="respondent-intro-toggle"
          aria-expanded={false}
          onClick={toggle}
        >
          <Chevron up={false} />
          {t("respondentIntroShow")}
        </button>
        {newButton}
      </section>
    );
  }

  return (
    <section className="respondent-intro" aria-labelledby="respondent-intro-title">
      <div className="respondent-intro-head">
        <div className="respondent-intro-lead">
          <h2 id="respondent-intro-title">{t("respondentIntroTitle")}</h2>
          <p>
            {t("respondentIntroText").replace("{location}", user?.location || t("allLocations"))}
          </p>
        </div>
        {newButton}
      </div>
      <ol className="respondent-intro-steps">
        {STEPS.map((step, index) => (
          <li key={step.status}>
            <span className={`respondent-intro-step-number ${step.status}`}>{index + 1}</span>
            <div>
              <div className="respondent-intro-step-title">
                <strong>{t(step.title)}</strong>
                <span className={`status-badge ${step.status}`}>{t(step.badge)}</span>
              </div>
              <p>{t(step.text)}</p>
            </div>
          </li>
        ))}
      </ol>
      <div className="respondent-intro-warning" role="note">
        <WarningIcon size={22} />
        <p>
          <strong>{t("respondentSensitiveTitle")}</strong> {t("respondentSensitiveText")}
        </p>
      </div>
      <div className="respondent-intro-footer">
        <span>{t("respondentContact")}</span>
        <button
          type="button"
          className="respondent-intro-toggle"
          aria-expanded={true}
          onClick={toggle}
        >
          {t("respondentIntroHide")}
          <Chevron up />
        </button>
      </div>
    </section>
  );
}
